import {
  DEFAULT_MATCH_CONFIG,
  DEFAULT_ROUND_CONFIG,
  type Attempt,
  type MatchState,
} from '@ranked-waiting/game-core';
import { renderRoundStage } from './round-stage';
import { formatDurationMs } from './format';
import { resultBadgeMarkup } from './result-badge';
import { accuracyBarMarkup } from './accuracy-bar';
import { applyCachedTimerTheme } from './cosmetics';
import { applyCachedTimerFaceArt } from './catalog';
import { checkForNewAchievements } from './achievement-toast';

export type PlayerSlot = 'a' | 'b';

interface PresenceMessage {
  type: 'presence';
  connected: Record<PlayerSlot, boolean>;
  ready: Record<PlayerSlot, boolean>;
}
interface JoinedMessage {
  type: 'joined';
  slot: PlayerSlot;
}
interface RoundTargetMessage {
  type: 'round-target';
  roundId: number;
  targetMs: number;
  prepareDelayMs: number;
  match: MatchState;
}
interface RoundResultMessage {
  type: 'round-result';
  roundId: number;
  attempts: Record<PlayerSlot, Attempt>;
  match: MatchState;
  ratingDeltas: Partial<Record<PlayerSlot, number>> | null;
}
interface OpponentLeftMessage {
  type: 'opponent-left';
}
/** Sent once a slot's socket closes on a match with something at stake (C005.2). The match is not over — a grace window has started. */
interface OpponentDisconnectedMessage {
  type: 'opponent-disconnected';
  graceMs: number;
}
/** Sent once the disconnected slot reconnects within its grace window. */
interface OpponentReconnectedMessage {
  type: 'opponent-reconnected';
}
/**
 * Sent to a socket that reconnected to a slot still within its grace window
 * (C005.2). Carries just enough to rebuild: the normal `presence` broadcast
 * that follows handles the lobby case, so this only needs to say anything
 * when a round was actually in progress.
 */
interface ResumedMessage {
  type: 'resumed';
  slot: PlayerSlot;
  match: MatchState;
  roundId: number;
  currentTargetMs: number | null;
  alreadyAttempted: boolean;
}
/** Sent when a grace window expired with no reconnect: the match is decided as an abandonment (C005.3). */
interface MatchForfeitedMessage {
  type: 'match-forfeited';
  match: MatchState;
  ratingDeltas: Partial<Record<PlayerSlot, number>> | null;
  outcome: 'forfeit' | 'double-forfeit';
}
interface RequeueRequiredMessage {
  type: 'requeue-required';
  message: string;
}
interface ErrorMessage {
  type: 'error';
  message: string;
}
type ServerMessage =
  | PresenceMessage
  | JoinedMessage
  | RoundTargetMessage
  | RoundResultMessage
  | OpponentLeftMessage
  | OpponentDisconnectedMessage
  | OpponentReconnectedMessage
  | ResumedMessage
  | MatchForfeitedMessage
  | RequeueRequiredMessage
  | ErrorMessage;

export interface DuelSessionOptions {
  lobbyLabel: string;
  lobbyHeading?: string;
  onExit: () => void;
}

/**
 * Wires a connected MatchRoom WebSocket to the shared lobby/round/result UI.
 * Used by both custom room-code duels and ranked matchmaking once a socket
 * to a MatchRoom is open — everything after "connected" is identical.
 */
export function attachDuelSession(mount: HTMLElement, socket: WebSocket, options: DuelSessionOptions): () => void {
  let mySlot: PlayerSlot | null = null;
  let destroyRound: (() => void) | void;
  let destroyed = false;
  /**
   * What the content area is currently showing. A `presence` broadcast may only
   * (re)render the lobby while we are *in* the lobby — the server broadcasts
   * presence on an opponent's mid-round disconnect/reconnect and on forfeit, and
   * an unconditional lobby render there used to wipe the live round stage (its
   * stop control with it — fatal on touch, where there is no keyboard fallback)
   * or the resume/result/forfeit screen. Rematch is the one path back to the
   * lobby from a finished match, and it sets this to 'lobby' explicitly on click.
   */
  let uiState: 'lobby' | 'round' | 'between' | 'complete' | 'forfeited' | 'error' | 'resume' = 'lobby';

  applyCachedTimerTheme(mount);
  void applyCachedTimerFaceArt(mount);

  // A banner that lives outside the content area so a reconnect notice never
  // wipes an in-progress round: every other render function below targets
  // `contentEl`, never `mount` directly.
  mount.innerHTML = `
    <div class="connection-banner" id="connection-banner" hidden></div>
    <div class="session-content" id="session-content"></div>
  `;
  const bannerEl = mount.querySelector<HTMLDivElement>('#connection-banner')!;
  const contentEl = mount.querySelector<HTMLDivElement>('#session-content')!;

  let graceDeadlineMs: number | null = null;
  let bannerIntervalId: number | undefined;

  function renderBanner() {
    if (graceDeadlineMs === null) {
      bannerEl.hidden = true;
      bannerEl.textContent = '';
      return;
    }
    const remainingSeconds = Math.max(0, Math.ceil((graceDeadlineMs - Date.now()) / 1000));
    bannerEl.hidden = false;
    bannerEl.textContent = `Opponent disconnected. Reconnecting in ${remainingSeconds}s…`;
  }

  function startDisconnectBanner(graceMs: number) {
    graceDeadlineMs = Date.now() + graceMs;
    renderBanner();
    if (bannerIntervalId !== undefined) clearInterval(bannerIntervalId);
    bannerIntervalId = window.setInterval(renderBanner, 1000);
  }

  function clearDisconnectBanner() {
    graceDeadlineMs = null;
    if (bannerIntervalId !== undefined) {
      clearInterval(bannerIntervalId);
      bannerIntervalId = undefined;
    }
    renderBanner();
  }

  function renderLobby(presence: PresenceMessage) {
    const meReady = mySlot ? presence.ready[mySlot] : false;
    const opponentSlot: PlayerSlot | null = mySlot === 'a' ? 'b' : mySlot === 'b' ? 'a' : null;
    const opponentConnected = opponentSlot ? presence.connected[opponentSlot] : false;

    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">${options.lobbyLabel}</p>
        ${options.lobbyHeading ? `<h2>${options.lobbyHeading}</h2>` : ''}
        <p class="round-stage__hint">${opponentConnected ? 'Opponent connected.' : 'Waiting for opponent to join…'}</p>
        <button class="button button--primary" type="button" data-action="ready" ${meReady ? 'disabled' : ''}>
          ${meReady ? "Waiting for opponent…" : "I'm ready"}
        </button>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="ready"]')?.addEventListener('click', () => {
      socket.send(JSON.stringify({ type: 'ready' }));
    });
  }

  function renderWaitingForOpponentStop() {
    contentEl.innerHTML = `<div class="handoff"><p class="handoff__label">Waiting for opponent to stop…</p></div>`;
  }

  /**
   * A minimal recovery screen for reconnecting mid-round (C005.2). It does not
   * try to reconstruct the original hidden-delay countdown — Ready re-arms
   * (a no-op if this slot had already armed before disconnecting) and Stop
   * reports `elapsedMs: null`, honestly deferring to the server's own
   * arm-to-stop measurement rather than guessing a number the client cannot
   * actually know after a reconnect. The server treats `null` identically to
   * omitting the field — see `parseClaimedElapsedMs` in game-core — so this is
   * never mistaken for a confident (if coincidentally plausible) claim.
   */
  function renderResumeRound(roundId: number, targetMs: number) {
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Reconnected. Round ${roundId} in progress</p>
        <h2>${formatDurationMs(targetMs)}s target</h2>
        <p class="round-stage__hint">Press Ready, then STOP the instant you believe the target has elapsed.</p>
        <button class="button button--primary" type="button" data-action="resume-ready">Ready</button>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="resume-ready"]')?.addEventListener('click', () => {
      socket.send(JSON.stringify({ type: 'arm', roundId }));
      contentEl.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Round ${roundId}</p>
          <button class="button button--primary" type="button" data-action="resume-stop">STOP</button>
        </div>
      `;
      contentEl.querySelector<HTMLButtonElement>('[data-action="resume-stop"]')?.addEventListener('click', () => {
        socket.send(JSON.stringify({ type: 'stop', roundId, elapsedMs: null, isFalseStart: false }));
        renderWaitingForOpponentStop();
      });
    });
  }

  function renderError(message: string) {
    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Error</p>
        <h2>${message}</h2>
        <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
      </div>
    `;
    contentEl.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => options.onExit());
  }

  function renderRoundResult(msg: RoundResultMessage) {
    if (!mySlot) return;
    const opponentSlot: PlayerSlot = mySlot === 'a' ? 'b' : 'a';
    const mine = msg.attempts[mySlot];
    const theirs = msg.attempts[opponentSlot];
    const lastRound = msg.match.history[msg.match.history.length - 1];
    const winnerLabel =
      lastRound?.winner === 'tie' ? 'Tie — no round point' : lastRound?.winner === mySlot ? 'You take the round' : 'Opponent takes the round';

    contentEl.innerHTML = `
      <div class="round-result-pair">
        <p class="handoff__label">${winnerLabel}</p>
        <div class="round-result-pair__grid">
          <div class="round-result${mine.isFalseStart ? ' round-result--false-start' : ''}">
            <div><span>You</span><strong>${mine.isFalseStart ? 'FALSE START' : `${formatDurationMs(mine.elapsedMs)}s`}</strong></div>
            <div><span>Result</span>${resultBadgeMarkup(mine)}</div>
            ${accuracyBarMarkup(mine)}
          </div>
          <div class="round-result${theirs.isFalseStart ? ' round-result--false-start' : ''}">
            <div><span>Opponent</span><strong>${theirs.isFalseStart ? 'FALSE START' : `${formatDurationMs(theirs.elapsedMs)}s`}</strong></div>
            <div><span>Result</span>${resultBadgeMarkup(theirs)}</div>
            ${accuracyBarMarkup(theirs)}
          </div>
        </div>
        <p class="handoff__label">Target was ${formatDurationMs(mine.targetMs)}s. Score ${msg.match.scores.a}:${msg.match.scores.b}</p>
        ${
          msg.match.phase === 'complete'
            ? renderMatchCompleteMarkup(msg.match, msg.ratingDeltas, { allowRematch: true })
            : `<button class="button button--primary" type="button" data-action="continue">Continue</button>`
        }
      </div>
    `;

    if (msg.match.phase === 'complete') {
      attachCompleteHandlers();
      checkForNewAchievements();
    } else {
      const button = contentEl.querySelector<HTMLButtonElement>('[data-action="continue"]');
      button?.addEventListener('click', () => {
        button.disabled = true;
        button.textContent = 'Waiting for opponent…';
        socket.send(JSON.stringify({ type: 'continue' }));
      });
    }
  }

  function renderMatchCompleteMarkup(
    match: MatchState,
    ratingDeltas: Partial<Record<PlayerSlot, number>> | null,
    options: { allowRematch: boolean; label?: string },
  ): string {
    const { allowRematch } = options;
    const label =
      options.label ??
      (match.winner === mySlot ? 'You win the match!' : match.winner === null ? 'Match ended level.' : 'Opponent wins the match.');
    const myDelta = mySlot && ratingDeltas ? ratingDeltas[mySlot] : undefined;
    const ratingLine = myDelta !== undefined ? `<p class="handoff__label">Rating ${myDelta >= 0 ? '+' : ''}${myDelta}</p>` : '';
    return `
      <h2>${label}</h2>
      ${ratingLine}
      <div class="hero-actions">
        ${allowRematch ? `<button class="button button--primary" type="button" data-action="rematch">Rematch</button>` : ''}
        <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
      </div>
    `;
  }

  function attachCompleteHandlers() {
    contentEl.querySelector<HTMLButtonElement>('[data-action="rematch"]')?.addEventListener('click', (event) => {
      (event.target as HTMLButtonElement).disabled = true;
      // Returning to the lobby is driven by the server's post-reset `presence`
      // broadcast, which is only honoured while `uiState === 'lobby'`. Opting in
      // here is what lets a rematch leave the completed-match screen.
      uiState = 'lobby';
      socket.send(JSON.stringify({ type: 'rematch' }));
    });
    contentEl.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => options.onExit());
  }

  /**
   * A match decided by abandonment (C005.3). No rematch offer: the vanished
   * player is gone from the room's memory the moment this fires, so a
   * rematch click here would set this player's own rematchReady flag and then
   * wait forever for a partner who is never coming back.
   */
  function renderForfeited(msg: MatchForfeitedMessage) {
    if (!mySlot) return;
    const label =
      msg.outcome === 'double-forfeit'
        ? 'Neither player reconnected in time. The match ended with no result.'
        : msg.match.winner === mySlot
          ? 'Your opponent disconnected and did not return — you win by forfeit.'
          : 'You did not reconnect in time — the match is forfeited.';

    contentEl.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Match ended</p>
        ${renderMatchCompleteMarkup(msg.match, msg.ratingDeltas, { allowRematch: false, label })}
      </div>
    `;
    attachCompleteHandlers();
  }

  function handleServerMessage(msg: ServerMessage) {
    if (destroyed) return;
    if (msg.type === 'joined') {
      mySlot = msg.slot;
      return;
    }
    if (msg.type === 'resumed') {
      mySlot = msg.slot;
      if (msg.match.phase === 'in_progress' && msg.currentTargetMs !== null && !msg.alreadyAttempted) {
        uiState = 'resume';
        renderResumeRound(msg.roundId, msg.currentTargetMs);
      }
      // Otherwise: nothing in progress worth rebuilding by hand — leave
      // `uiState` as 'lobby' so the `presence` broadcast that follows this
      // message renders the lobby.
      return;
    }
    if (msg.type === 'presence') {
      // Only ever (re)render the lobby while we are actually in it. Mid-round,
      // on the resume screen, or on a result/forfeit screen, a presence
      // broadcast (opponent disconnect/reconnect/forfeit) must not wipe what's
      // shown. Rematch re-opts into 'lobby' so it can still bring us back.
      if (uiState !== 'lobby') return;
      if (destroyRound) {
        destroyRound();
        destroyRound = undefined;
      }
      renderLobby(msg);
      return;
    }
    if (msg.type === 'opponent-disconnected') {
      startDisconnectBanner(msg.graceMs);
      return;
    }
    if (msg.type === 'opponent-reconnected') {
      clearDisconnectBanner();
      return;
    }
    if (msg.type === 'match-forfeited') {
      clearDisconnectBanner();
      if (destroyRound) destroyRound();
      destroyRound = undefined;
      uiState = 'forfeited';
      renderForfeited(msg);
      return;
    }
    if (msg.type === 'round-target') {
      uiState = 'round';
      if (destroyRound) destroyRound();
      destroyRound = renderRoundStage(contentEl, {
        config: DEFAULT_ROUND_CONFIG,
        fixedTargetMs: msg.targetMs,
        fixedPrepareDelayMs: msg.prepareDelayMs,
        label: `Round ${msg.roundId} of ${DEFAULT_MATCH_CONFIG.maxRounds}`,
        onArm: () => socket.send(JSON.stringify({ type: 'arm', roundId: msg.roundId })),
        // Report the stop the instant it happens. The server measures the attempt
        // by when this arrives, so it must not wait on any player interaction.
        // The authoritative reveal arrives back as `round-result`.
        onStop: (attempt) => {
          socket.send(
            JSON.stringify({
              type: 'stop',
              roundId: msg.roundId,
              elapsedMs: attempt.elapsedMs,
              isFalseStart: attempt.isFalseStart,
            }),
          );
          if (destroyRound) destroyRound();
          destroyRound = undefined;
          renderWaitingForOpponentStop();
        },
      });
      return;
    }
    if (msg.type === 'round-result') {
      uiState = msg.match.phase === 'complete' ? 'complete' : 'between';
      renderRoundResult(msg);
      return;
    }
    if (msg.type === 'opponent-left') {
      uiState = 'error';
      renderError('Your opponent disconnected.');
      return;
    }
    if (msg.type === 'requeue-required') {
      uiState = 'error';
      renderError(msg.message);
      return;
    }
    if (msg.type === 'error') {
      uiState = 'error';
      renderError(msg.message);
    }
  }

  socket.onmessage = (event) => {
    try {
      handleServerMessage(JSON.parse(event.data as string) as ServerMessage);
    } catch {
      // ignore malformed frames
    }
  };
  socket.onerror = () => renderError('Could not reach the match server. Is it running?');

  return function destroy() {
    destroyed = true;
    if (destroyRound) destroyRound();
    if (bannerIntervalId !== undefined) clearInterval(bannerIntervalId);
    socket.onmessage = null;
    socket.onerror = null;
  };
}
